// Shared PDF export for Ask results. Used by AskInsightsPanel and AskBriefDialog.
// Uses html2canvas + jspdf (both already in the project) to snapshot a DOM node
// into a paginated A4 PDF.
export async function downloadAskPdf(opts: {
  element: HTMLElement;
  filenameSlug: string;
  headerTitle?: string;
}): Promise<void> {
  const { element, filenameSlug, headerTitle = 'CultureLens — Ask brief' } = opts;
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  // Let charts settle.
  await new Promise((r) => setTimeout(r, 100));
  const canvas = await html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: 2,
    useCORS: true,
  });

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 36;
  const usableW = pageWidth - margin * 2;
  const imgH = (canvas.height * usableW) / canvas.width;

  const drawHeader = (cont: boolean) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text(cont ? `${headerTitle} (cont.)` : headerTitle, margin, margin);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text(new Date().toLocaleString(), margin, margin + 14);
    pdf.setTextColor(0);
  };

  const contentTop = margin + 28;
  const maxPageH = pageHeight - contentTop - margin;

  drawHeader(false);
  if (imgH <= maxPageH) {
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, contentTop, usableW, imgH);
  } else {
    const pxPerPt = canvas.width / usableW;
    const sliceHeightPx = Math.floor(maxPageH * pxPerPt);
    let y = 0;
    let first = true;
    while (y < canvas.height) {
      const h = Math.min(sliceHeightPx, canvas.height - y);
      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = h;
      const ctx = slice.getContext('2d');
      if (ctx) ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
      if (!first) {
        pdf.addPage();
        drawHeader(true);
      }
      pdf.addImage(
        slice.toDataURL('image/png'),
        'PNG',
        margin,
        first ? contentTop : margin + 28,
        usableW,
        (h / canvas.width) * usableW,
      );
      y += h;
      first = false;
    }
  }

  const safeSlug = (filenameSlug || 'ask-report')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'ask-report';
  pdf.save(`culturelens-${safeSlug}.pdf`);
}
